import React, { useMemo, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AlertCircle, FileText, Loader, RefreshCw } from 'lucide-react';
import { EmptyState } from '../../components/ui/EmptyState';
import { JobType } from '../../graphql/types';
import type { UploadCredentials } from '../../graphql/types';
import { TOP_BAR_HEIGHT_PX } from '../TopBar';
import { readWorkspaceParams, writeWorkspaceParams } from '../urlState';
import { useWorkflowWorkspace } from '../WorkflowWorkspace';
import { ExtractMode } from './ExtractMode';
import { resolveExtractSelection, sortDocuments } from './extractModel';

/**
 * ExtractBody: the Extract Mode body of the DD/BA Workspace (board `1a`).
 *
 * Takes the job's loans, files and documents from the Workspace (loaded once
 * for the Rail and every Mode), picks the file to show from the `loan` and
 * `doc` URL params (default: the first loan with extracted documents), and
 * renders `ExtractMode`. The Rail is the Workspace's (`WorkflowRail`): choosing
 * a loan (DD) or a document (BA) there switches the file. When a loan has
 * several files, a file switcher above the viewer moves between them by
 * setting `doc`, so the choice stays in the URL.
 *
 * The upload bucket and credentials come from `location.state`, as the legacy
 * job page reads them; without them the viewer uses the environment bucket and
 * the identity pool.
 *
 * The field counts for the Top_Bar are reported by the Rail.
 *
 * Requirements: 3.1, 3.3, 3.4, 7.1, 7.2, 7.3.
 */

const FRAME_STYLE = { height: `calc(100vh - ${TOP_BAR_HEIGHT_PX}px)` };

interface LocationState {
  uploadBucket?: string;
  uploadCredentials?: UploadCredentials | null;
}

export const ExtractBody: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { jobType, data: workspaceData, refreshData, notifyConfirmationsChanged } =
    useWorkflowWorkspace();
  const feature = jobType === JobType.BA ? JobType.BA : JobType.DD;

  // Upload credentials arrive once in the navigation state; later URL changes
  // inside the Workspace drop it, so the first value seen is kept.
  const navState = useRef<LocationState | null>(null);
  const incoming = location.state as LocationState | null;
  if (incoming && (incoming.uploadBucket || incoming.uploadCredentials)) navState.current = incoming;
  const uploadBucket = navState.current?.uploadBucket || undefined;
  const uploadCredentials = navState.current?.uploadCredentials ?? null;

  const data = workspaceData.status === 'ready' ? workspaceData.data : null;

  // ── Selection ─────────────────────────────────────────────────────────────
  const params = readWorkspaceParams(searchParams, feature);
  const selection = useMemo(
    () =>
      data
        ? resolveExtractSelection(data, { loan: params.loan, doc: params.doc, field: params.field })
        : null,
    [data, params.loan, params.doc, params.field]
  );

  // ── States ────────────────────────────────────────────────────────────────
  if (workspaceData.status === 'loading') {
    return (
      <div
        role="status"
        data-testid="extract-loading"
        className="flex min-h-[400px] flex-col items-center justify-center"
      >
        <Loader className="mb-4 h-10 w-10 animate-spin text-[#834DFB]" aria-hidden="true" />
        <p className="text-gray-600">Loading extracted fields...</p>
      </div>
    );
  }

  if (workspaceData.status === 'error' || !data || !selection) {
    return (
      <div role="alert" className="max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6">
        <div className="flex items-start">
          <AlertCircle className="h-6 w-6 shrink-0 text-red-500" aria-hidden="true" />
          <div className="ml-4 flex-1">
            <h2 className="text-lg font-bold text-red-800">The extracted fields could not be loaded</h2>
            <p className="mt-2 text-red-700">Check your connection and try again.</p>
            <button
              type="button"
              onClick={() => void refreshData(true)}
              className="mt-4 inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const toIntake = (
    <Link
      to={{ search: `?${writeWorkspaceParams(searchParams, { mode: 'intake', doc: null, field: null, exception: null }).toString()}` }}
      state={location.state}
      className="inline-flex items-center rounded-lg bg-[#834DFB] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2"
    >
      Go to Intake
    </Link>
  );

  // Nothing extracted anywhere in the job.
  if (data.documents.length === 0) {
    return (
      <EmptyState
        className="mx-auto max-w-xl"
        icon={<FileText size={22} strokeWidth={2} aria-hidden="true" />}
        title="Nothing has been extracted yet"
        description="Add files in Intake and start extraction. The fields found in each document appear here for review."
        action={toIntake}
      />
    );
  }

  // The chosen loan has no extracted documents (another loan does).
  if (!selection.file) {
    return (
      <div data-testid="extract-empty-loan" className="-m-6 min-w-0 bg-[#f7f6fb]" style={FRAME_STYLE}>
        <div className="h-full min-w-0 overflow-y-auto p-6">
          <EmptyState
            className="mx-auto max-w-xl"
            icon={<FileText size={22} strokeWidth={2} aria-hidden="true" />}
            title="Nothing has been extracted for this loan yet"
            description="Add files for this loan in Intake and start extraction, or pick another loan."
            action={toIntake}
          />
        </div>
      </div>
    );
  }

  // File switcher: only when the loan has more than one file with documents.
  const switchFile = (fileId: string) => {
    const first = sortDocuments(selection.loanDocuments.filter((d) => d.fileId === fileId))[0];
    setSearchParams(
      writeWorkspaceParams(searchParams, {
        loan: selection.loanId,
        doc: first?.documentId ?? null,
        field: null,
        exception: null,
      })
    );
  };

  const viewerHeader =
    selection.loanFiles.length > 1 ? (
      <nav
        aria-label="Files in this loan"
        data-testid="file-switcher"
        className="flex shrink-0 items-center gap-2 overflow-x-auto border-b border-gray-200 bg-white px-4 py-2"
      >
        <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
          File {selection.loanFiles.findIndex((f) => f.fileId === selection.file!.fileId) + 1} of{' '}
          {selection.loanFiles.length}
        </span>
        {selection.loanFiles.map((f) => {
          const active = f.fileId === selection.file!.fileId;
          return (
            <button
              key={f.fileId}
              type="button"
              aria-pressed={active}
              title={f.filename}
              onClick={() => (active ? undefined : switchFile(f.fileId))}
              className={`max-w-[200px] shrink-0 truncate rounded-full border px-3 py-1 text-xs font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] ${
                active
                  ? 'border-[#834DFB] bg-[#EDE7FF] text-[#5A21C9]'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.filename}
            </button>
          );
        })}
      </nav>
    ) : null;

  return (
    <ExtractMode
      key={selection.file.fileId}
      file={selection.file}
      documents={selection.documents}
      uploadBucket={uploadBucket}
      uploadCredentials={uploadCredentials}
      feature={feature}
      viewerHeader={viewerHeader}
      onConfirmationsChange={notifyConfirmationsChanged}
    />
  );
};

export default ExtractBody;
